import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaImage } from 'react-icons/fa';
import API_BASE_URL, { ASSET_BASE_URL } from '../../config/api';

const PaymentVerification = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ by_grade: [], by_month: [], today_revenue: 0, total_overall: 0 });
  const [loading, setLoading] = useState(true);
  const [showSummary, setShowSummary] = useState(false); // Dropdown/Accordion state

  const fetchPayments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/manage_payments.php`);
      const data = await response.json();
      if (data.status === 'success') {
        setPayments(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/get_revenue_summary.php`);
      const data = await res.json();
      if (data.status === 'success') {
        setSummary(data.data);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    fetchPayments();
    fetchSummary();

    const interval = setInterval(() => {
      fetchPayments();
      fetchSummary();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleStatusUpdate = async (paymentId, status) => {
    try {
      const admin = JSON.parse(localStorage.getItem('user'));
      const response = await fetch(`${API_BASE_URL}/admin/manage_payments.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentId, status, adminId: admin?.id })
      });
      const data = await response.json();
      if (data.status === 'success') {
        fetchPayments();
        fetchSummary();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-bold text-slate-800">Payment Verification</h2>
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="bg-orange-50 px-4 py-2 rounded-2xl border border-orange-100 text-right flex-grow sm:flex-grow-0">
             <p className="text-[9px] font-black text-orange-400 uppercase tracking-widest">Today's Income</p>
             <p className="text-lg font-black text-orange-600">LKR {summary.today_revenue.toLocaleString()}</p>
          </div>
          <div className="bg-teal-50 px-4 py-2 rounded-2xl border border-teal-100 text-right flex-grow sm:flex-grow-0">
             <p className="text-[9px] font-black text-teal-400 uppercase tracking-widest">Total Revenue</p>
             <p className="text-lg font-black text-teal-600">LKR {summary.total_overall.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Revenue Summary Dropdown / Accordion */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <button 
          onClick={() => setShowSummary(!showSummary)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
               <FaImage size={14} />
            </div>
            <span className="font-black text-xs text-slate-700 uppercase tracking-widest">Revenue Summary & Analytics</span>
          </div>
          <div className="flex items-center gap-4">
             <a 
               href={`${API_BASE_URL}/admin/download_report.php`} 
               onClick={(e) => e.stopPropagation()} // Prevent accordion from toggling
               className="px-3 py-1.5 bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-slate-900 transition-all flex items-center gap-2"
             >
               Download Full Report
             </a>
             <span className={`transform transition-transform ${showSummary ? 'rotate-180' : ''}`}>▼</span>
          </div>
        </button>

        {showSummary && (
          <div className="p-6 pt-0 border-t border-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Grade-wise Summary */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">By Grade</h3>
                <div className="bg-slate-50/50 rounded-2xl p-4 border border-slate-100">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200">
                        <th className="pb-2">Grade</th>
                        <th className="pb-2">Count</th>
                        <th className="pb-2 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {summary.by_grade.map((row, idx) => (
                        <tr key={idx} className="text-xs font-bold text-slate-600">
                          <td className="py-2.5">{row.grade}</td>
                          <td className="py-2.5">{row.payment_count}</td>
                          <td className="py-2.5 text-right text-teal-600">LKR {parseFloat(row.total_revenue).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Month-wise Summary */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">By Month</h3>
                <div className="bg-slate-50/50 rounded-2xl p-4 border border-slate-100">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200">
                        <th className="pb-2">Month</th>
                        <th className="pb-2">Count</th>
                        <th className="pb-2 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {summary.by_month.map((row, idx) => (
                        <tr key={idx} className="text-xs font-bold text-slate-600">
                          <td className="py-2.5">{row.month_name}</td>
                          <td className="py-2.5">{row.payment_count}</td>
                          <td className="py-2.5 text-right text-orange-600">LKR {parseFloat(row.total_revenue).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Student / Course</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Amount</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Proof</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium italic">No payment submissions found.</td>
                </tr>
              ) : payments.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-bold text-slate-800 text-sm">{p.student_name}</p>
                      <p className="text-xs text-orange-500 font-bold">
                        {p.course_title} {p.month_name ? `(${p.month_name})` : '(Full)'}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-bold text-slate-600 text-sm">LKR {p.amount}</td>
                  <td className="px-6 py-4">
                    <a 
                      href={`${ASSET_BASE_URL}/uploads/slips/${p.payment_proof}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:underline text-xs font-bold"
                    >
                      <FaImage /> View Slip
                    </a>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                      p.status === 'approved' ? 'bg-green-50 text-green-600' : 
                      p.status === 'rejected' ? 'bg-red-50 text-red-600' : 
                      'bg-yellow-50 text-yellow-600'
                    }`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {p.status === 'pending' && (
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => handleStatusUpdate(p.id, 'approved')}
                          className="p-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Approve"
                        ><FaCheck /></button>
                        <button 
                          onClick={() => handleStatusUpdate(p.id, 'rejected')}
                          className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Reject"
                        ><FaTimes /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentVerification;
